import {
  type BlackAndWhiteAction,
  type BlackAndWhiteState,
  type BlackAndWhiteViewerState,
  filterStateForViewer,
  reduceGameState,
  validateAction,
} from "@cloudnest/black-and-white-engine";
import {
  type BlackAndWhiteReplayEvent,
  toBlackAndWhiteReplayEvent,
} from "./event-adapter";
import type { BlackAndWhiteReplayFixtureEvent } from "./replay-fixtures.generated";

export interface BlackAndWhiteReplaySnapshot {
  readonly index: number;
  readonly event: BlackAndWhiteReplayFixtureEvent;
  readonly replayEvent: BlackAndWhiteReplayEvent;
  readonly state: BlackAndWhiteState;
  readonly viewerState: BlackAndWhiteViewerState;
}

export interface BuildBlackAndWhiteReplayOptions {
  readonly observerUserId?: string;
}

function requireState(
  state: BlackAndWhiteState | null,
  eventType: BlackAndWhiteReplayEvent["eventType"],
): BlackAndWhiteState {
  if (state === null) {
    throw new Error(`${eventType} before init`);
  }
  return state;
}

function toAction(event: BlackAndWhiteReplayEvent): BlackAndWhiteAction | null {
  switch (event.eventType) {
    case "init":
    case "SyncViewerState":
      return null;
    case "SubmitTile":
      return {
        type: "SubmitTile",
        playerId: event.userId,
        tile: event.payload.tile,
      };
  }
}

function applyReplayEvent(
  state: BlackAndWhiteState | null,
  event: BlackAndWhiteReplayEvent,
): BlackAndWhiteState {
  if (event.eventType === "init") {
    return event.payload.state;
  }

  const current = requireState(state, event.eventType);
  if (event.eventType === "SyncViewerState") {
    return current;
  }

  const action = toAction(event);
  if (action === null) {
    return current;
  }

  const validation = validateAction(current, action);
  if (!validation.valid) {
    throw new Error(
      `${event.eventType} rejected: ${validation.error ?? "Validation failed"}`,
    );
  }

  return reduceGameState(current, action);
}

export function buildBlackAndWhiteReplaySnapshots(
  events: readonly BlackAndWhiteReplayFixtureEvent[],
  options: BuildBlackAndWhiteReplayOptions = {},
): readonly BlackAndWhiteReplaySnapshot[] {
  const observerUserId = options.observerUserId ?? "observer";
  const snapshots: BlackAndWhiteReplaySnapshot[] = [];
  let state: BlackAndWhiteState | null = null;

  for (const event of events) {
    const replayEvent = toBlackAndWhiteReplayEvent(event);
    state = applyReplayEvent(state, replayEvent);
    snapshots.push({
      index: snapshots.length,
      event,
      replayEvent,
      state,
      viewerState: filterStateForViewer(state, observerUserId),
    });
  }

  if (state === null) {
    throw new Error("Cannot replay an empty Black and White event log");
  }

  return snapshots;
}
